import type { ColumnMetaConfig } from "@/types/central/ui";

/**
 * Standardized Table Cell Styles
 *
 * These constants ensure consistent styling across all data tables in the application.
 * Reference implementation: app/(main)/samples/page.tsx
 *
 * Usage:
 * ```tsx
 * import { TABLE_CELL_STYLES } from "@/lib/utils/table-styles";
 *
 * // Primary cell (clickable name/title)
 * <span className={TABLE_CELL_STYLES.primary}>{name}</span>
 *
 * // Regular text cell
 * <span className={TABLE_CELL_STYLES.regular}>{value}</span>
 *
 * // Secondary/date cell
 * <span className={TABLE_CELL_STYLES.secondary}>{date}</span>
 *
 * // Empty/placeholder
 * <span className={TABLE_CELL_STYLES.empty}>—</span>
 * ```
 */

/**
 * Core table cell text styles
 * - primary: For clickable name/title cells (first column typically)
 * - regular: For standard text content
 * - secondary: For dates, metadata, less prominent text
 * - empty: For empty states, placeholders, em-dashes
 */
export const TABLE_CELL_STYLES = {
  /** Primary clickable cell (name/title) - includes hover states for row click */
  primary:
    "font-semibold text-gray-700 group-hover:text-gray-900 cursor-pointer transition-colors",

  /** Regular text cell content */
  regular: "text-gray-700",

  /** Regular text with word-wrap for long content */
  regularTruncate: "text-gray-700 break-words",

  /** Secondary/date text - smaller and less prominent */
  secondary: "text-sm text-gray-600",

  /** Empty state / placeholder text */
  empty: "text-text-tertiary text-sm",
} as const;

/**
 * Column size guidelines for consistent table layouts
 * These are recommended sizes in pixels for common column types
 */
export const TABLE_COLUMN_SIZES = {
  /** Primary name/title column */
  primary: 220,

  /** Primary column - wider variant for longer names */
  primaryWide: 280,

  /** Badge columns (status, role, type) */
  badge: 120,

  /** Badge columns - wider variant */
  badgeWide: 150,

  /** Date/time columns */
  date: 160,

  /** Date columns - wider for full datetime */
  dateWide: 180,

  /** User/creator columns */
  user: 150,

  /** Description/address columns */
  description: 200,

  /** Actions column */
  actions: 100,

  /** Narrow columns for simple values */
  narrow: 100,
} as const;

/**
 * Avatar sizes for consistency in user cells
 */
export const TABLE_AVATAR_SIZES = {
  /** Standard avatar size */
  default: "h-10 w-10",

  /** Small avatar size */
  small: "h-8 w-8",

  /** Large avatar size */
  large: "h-12 w-12",
} as const;

export type TableCellStyle = keyof typeof TABLE_CELL_STYLES;
export type TableColumnSize = keyof typeof TABLE_COLUMN_SIZES;
export type TableAvatarSize = keyof typeof TABLE_AVATAR_SIZES;

/**
 * Cell overflow classes from column `textBehavior`.
 * Default is single-line (`whitespace-nowrap`) so the table grows and
 * horizontal scroll handles overflow instead of wrapping.
 */
export function getCellTextClass(meta?: ColumnMetaConfig): string {
  switch (meta?.textBehavior) {
    case "wrap":
      return "whitespace-normal break-words";
    case "clamp-2":
      return "line-clamp-2";
    case "clamp-3":
      return "line-clamp-3";
    case "truncate":
      return "truncate";
    default:
      return "whitespace-nowrap";
  }
}

/** `min-w-0` lets truncate/clamp/wrap shrink; nowrap cells must keep content width. */
export function cellContentNeedsMinWidthZero(meta?: ColumnMetaConfig): boolean {
  const behavior = meta?.textBehavior;
  return (
    behavior === "truncate" ||
    behavior === "wrap" ||
    behavior === "clamp-2" ||
    behavior === "clamp-3"
  );
}

/** TanStack default column size; treated as "unset" for min-width. */
const TANSTACK_DEFAULT_COLUMN_SIZE = 150;

/**
 * Width constraints for table header/body cells.
 * Truncate/wrap/clamp columns honor `meta.maxSize` so ellipsis can apply.
 * A column whose `size` and `maxSize` are the same pixel value is locked to
 * that width even when `textBehavior` is unset, so it does not absorb leftover
 * table width. Other columns keep a floor only and stay fluid.
 */
export function getColumnSizeStyle(
  size: number,
  meta?: ColumnMetaConfig,
): { width?: number; minWidth?: number; maxWidth?: number } {
  const locksToSize = meta?.maxSize != null && meta.maxSize === size;
  const minWidth = size !== TANSTACK_DEFAULT_COLUMN_SIZE ? size : undefined;
  const maxWidth =
    meta?.maxSize != null && (locksToSize || cellContentNeedsMinWidthZero(meta))
      ? meta.maxSize
      : undefined;

  return {
    ...(locksToSize ? { width: size } : {}),
    minWidth,
    maxWidth,
  };
}
